import { isDiscover, isMastercard } from "./helpers";

/**
 * Card brand detection based on BIN (Bank Identification Number) prefix ranges.
 *
 * Requirements: 10.2, 10.3
 */

export type CardBrand = "VISA" | "MASTERCARD" | "AMEX" | "DISCOVER" | "ELO";

export interface BrandInfo {
  brand: CardBrand;
  panLengths: number[];
  cvcLength: number;
}

/** Known ELO BIN prefixes */
const ELO_BINS: string[] = [
  "636368", "438935", "504175", "451416", "636297",
  "506699", "509048", "509067", "509049", "509069",
  "509050", "509074", "509068", "509040", "509045",
  "509051", "509046", "509066", "509047", "509042",
  "509052", "509043", "509064", "509044",
];

export const BrandDetector = {
  /**
   * Detect card brand from a card number string.
   * Returns BrandInfo if a known brand is detected, null otherwise.
   */
  detect(cardNumber: string): BrandInfo | null {
    if (!cardNumber || !/^\d+$/.test(cardNumber)) {
      return null;
    }

    // ELO — check specific 6-digit BIN prefixes (must check before VISA/Discover)
    if (cardNumber.length >= 6) {
      const bin6 = cardNumber.substring(0, 6);
      if (ELO_BINS.includes(bin6)) {
        return { brand: "ELO", panLengths: [16], cvcLength: 3 };
      }
    }

    // AMEX — starts with 34 or 37
    if (/^3[47]/.test(cardNumber)) {
      return { brand: "AMEX", panLengths: [15], cvcLength: 4 };
    }

    // DISCOVER — 6011, 622126-622925, 644-649, 65
    if (isDiscover(cardNumber)) {
      return { brand: "DISCOVER", panLengths: [16, 17, 18, 19], cvcLength: 3 };
    }

    // MASTERCARD — 51-55, 2221-2720
    if (isMastercard(cardNumber)) {
      return { brand: "MASTERCARD", panLengths: [16], cvcLength: 3 };
    }

    // VISA — starts with 4
    if (cardNumber.startsWith("4")) {
      return { brand: "VISA", panLengths: [13, 16, 19], cvcLength: 3 };
    }

    return null;
  },

  /**
   * Check if a card number has a valid PAN length for the detected brand.
   */
  isValidLength(cardNumber: string, brandInfo: BrandInfo): boolean {
    return brandInfo.panLengths.includes(cardNumber.length);
  },
};
